"use client";

import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  TaskStatus,
  TaskV2,
  getTalentTasksV2,
  getApplicationTasksV2,
  addTaskV2,
  updateTaskV2,
  deleteTaskV2
} from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loading } from "@/components/ui/loading";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Clock,
  CheckCircle2,
  ArrowUpCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface ApplicationOrCandidateTasksProps {
  isTalentProfile: boolean;
  talentOrApplicationId: string;
}

export function ApplicationOrCandidateTasks({
  isTalentProfile,
  talentOrApplicationId
}: ApplicationOrCandidateTasksProps) {
  const { t } = useTranslation(["common"]);
  const { toast } = useToast();
  const [tasks, setTasks] = useState<TaskV2[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskV2 | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: 1,
    status: TaskStatus.OPEN
  });

  const fetchTasks = useCallback(async () => {
    if (!talentOrApplicationId) return;
    setLoading(true);
    try {
      const data = isTalentProfile
        ? await getTalentTasksV2(talentOrApplicationId)
        : await getApplicationTasksV2(talentOrApplicationId);
      setTasks(data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast({
        title: t("status.error"),
        description: t("tasks.loading")
      });
    } finally {
      setLoading(false);
    }
  }, [isTalentProfile, talentOrApplicationId, toast, t]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleOpenDialog = (task?: TaskV2) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || 1,
        status: task.status
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: "",
        description: "",
        priority: 1,
        status: TaskStatus.OPEN
      });
    }
    setIsDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast({
        title: t("tasks.messages.validation_error"),
        description: t("tasks.messages.required_fields"),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingTask) {
        // Update
        const updatedTask = await updateTaskV2(editingTask.id, {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          status: formData.status
        });
        setTasks((prev) =>
          prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
        );
        toast({
          title: t("status.success"),
          description: t("tasks.messages.update_success")
        });
      } else {
        // Create
        const newTaskData = {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          status: formData.status,
          [isTalentProfile ? "talent_profile" : "application"]:
            talentOrApplicationId
        };
        const newTask = await addTaskV2(newTaskData as any);
        setTasks((prev) => [newTask, ...prev]);
        toast({
          title: t("status.success"),
          description: t("tasks.messages.save_success")
        });
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving task:", error);
      toast({
        title: t("status.error"),
        description: t("tasks.messages.save_error"),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTask = async (id: string) => {
    if (!window.confirm(t("tasks.messages.confirm_delete"))) return;
    try {
      await deleteTaskV2(id);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      toast({
        title: t("status.success"),
        description: t("tasks.messages.delete_success")
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast({
        title: t("status.error"),
        description: t("tasks.messages.delete_error"),
        variant: "destructive"
      });
    }
  };

  const groupedTasks = useMemo(() => {
    return {
      open: tasks.filter((t) => t.status === TaskStatus.OPEN),
      inProgress: tasks.filter((t) => t.status === TaskStatus.IN_PROGRESS),
      done: tasks.filter((t) => t.status === TaskStatus.DONE)
    };
  }, [tasks]);

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loading size="sm" text={t("tasks.loading")} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">{t("tasks.title")}</h3>
        <Button
          onClick={() => handleOpenDialog()}
          size="sm"
          className="gap-2 bg-primary hover:bg-primary/90 shadow-sm transition-all active:scale-95"
        >
          <Plus className="h-4 w-4" />
          {t("tasks.new_task")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* OPEN COLUMN */}
        <TaskColumn
          title={t("tasks.columns.open")}
          tasks={groupedTasks.open}
          icon={<AlertCircle className="h-5 w-5 text-gray-400" />}
          onEdit={handleOpenDialog}
          onDelete={handleDeleteTask}
          emptyText={t("tasks.empty_state")}
        />

        {/* IN PROGRESS COLUMN */}
        <TaskColumn
          title={t("tasks.columns.in_progress")}
          tasks={groupedTasks.inProgress}
          icon={<Clock className="h-5 w-5 text-amber-500" />}
          onEdit={handleOpenDialog}
          onDelete={handleDeleteTask}
          emptyText={t("tasks.empty_state")}
        />

        {/* DONE COLUMN */}
        <TaskColumn
          title={t("tasks.columns.done")}
          tasks={groupedTasks.done}
          icon={<CheckCircle2 className="h-5 w-5 text-green-500" />}
          onEdit={handleOpenDialog}
          onDelete={handleDeleteTask}
          emptyText={t("tasks.empty_state")}
        />
      </div>

      {/* Task Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] border-none shadow-2xl rounded-2xl">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {editingTask
                ? t("tasks.form.edit_title")
                : t("tasks.form.create_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-6">
            <div className="grid gap-2">
              <label className="text-sm font-semibold text-gray-700">
                {t("tasks.form.title_label")}
              </label>
              <Input
                placeholder={
                  editingTask ? "" : t("tasks.form.title_placeholder")
                }
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                className="focus-visible:ring-primary h-11"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-semibold text-gray-700">
                {t("tasks.form.description_label")}
              </label>
              <Textarea
                placeholder={t("tasks.form.description_placeholder")}
                className="min-h-[120px] resize-none focus-visible:ring-primary"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-semibold text-gray-700">
                  {t("tasks.form.priority_label")}
                </label>
                <Select
                  value={String(formData.priority)}
                  onValueChange={(val) =>
                    setFormData((prev) => ({ ...prev, priority: Number(val) }))
                  }
                >
                  <SelectTrigger className="h-11 focus:ring-primary">
                    <SelectValue placeholder={t("tasks.form.priority_label")} />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((p) => (
                      <SelectItem key={p} value={String(p)}>
                        {p}{" "}
                        {p >= 7
                          ? `(${t("tasks.priorities.high")})`
                          : p >= 4
                            ? `(${t("tasks.priorities.medium")})`
                            : `(${t("tasks.priorities.normal")})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-semibold text-gray-700">
                  {t("tasks.form.status_label")}
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(val) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: val as TaskStatus
                    }))
                  }
                >
                  <SelectTrigger className="h-11 focus:ring-primary">
                    <SelectValue placeholder={t("tasks.form.status_label")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TaskStatus.OPEN}>
                      {t("tasks.columns.open")}
                    </SelectItem>
                    <SelectItem value={TaskStatus.IN_PROGRESS}>
                      {t("tasks.columns.in_progress")}
                    </SelectItem>
                    <SelectItem value={TaskStatus.DONE}>
                      {t("tasks.columns.done")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSubmitting}
              className="rounded-lg h-11"
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              onClick={handleSaveTask}
              disabled={isSubmitting}
              className="rounded-lg h-11 bg-primary hover:bg-primary/90"
            >
              {isSubmitting
                ? t("status.saving")
                : editingTask
                  ? t("tasks.form.update")
                  : t("tasks.form.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TaskColumnProps {
  title: string;
  tasks: TaskV2[];
  icon: React.ReactNode;
  onEdit: (task: TaskV2) => void;
  onDelete: (id: string) => void;
  emptyText: string;
}

function TaskColumn({
  title,
  tasks,
  icon,
  onEdit,
  onDelete,
  emptyText
}: TaskColumnProps) {
  return (
    <div className="flex flex-col h-full min-h-[500px] bg-gray-50/50 rounded-2xl p-4 border border-gray-100">
      <div className="flex items-center gap-2 mb-4 px-1">
        {icon}
        <h4 className="font-bold text-gray-700">{title}</h4>
        <Badge
          variant="secondary"
          className="ml-auto bg-white border-gray-200 text-gray-500 text-[10px] px-1.5 py-0"
        >
          {tasks.length}
        </Badge>
      </div>

      <div className="space-y-3 flex-1">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onEdit={() => onEdit(task)}
            onDelete={() => onDelete(task.id)}
          />
        ))}
        {tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-gray-200 rounded-xl bg-white/50">
            <p className="text-xs text-gray-400 italic">{emptyText}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({
  task,
  onEdit,
  onDelete
}: {
  task: TaskV2;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["common"]);
  const getPriorityColor = (priority: number) => {
    if (priority >= 7) return "border-l-red-500 shadow-red-50/50";
    if (priority >= 4) return "border-l-amber-500 shadow-amber-50/50";
    return "border-l-blue-400 shadow-blue-50/50";
  };

  return (
    <div
      className={cn(
        "group relative p-4 bg-white border border-gray-100 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:translate-y-[-2px] border-l-4",
        getPriorityColor(task.priority || 1)
      )}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0 pr-2">
          <h5 className="font-bold text-gray-900 leading-tight truncate">
            {task.title || t("tasks.card.untitled")}
          </h5>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-gray-400 font-medium">
              {new Date(task.created_at).toLocaleDateString([], {
                month: "short",
                day: "numeric"
              })}
            </span>
            {task.priority >= 4 && (
              <div
                className={cn(
                  "flex items-center gap-0.5 text-xs font-semibold",
                  task.priority >= 7 ? "text-red-600" : "text-amber-600"
                )}
              >
                <ArrowUpCircle className="h-3 w-3" />
                <span className="text-[10px] uppercase tracking-wider">
                  {task.priority >= 7
                    ? t("tasks.priorities.high")
                    : t("tasks.priorities.medium")}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-full"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
            onClick={onDelete}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {task.description && (
        <p className="text-sm text-gray-600 line-clamp-2 mt-2 leading-relaxed">
          {task.description}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="flex -space-x-1 overflow-hidden">
          {/* Mock avatar for assigned by */}
          <div className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center">
            <span className="text-[10px] font-bold text-gray-400">A</span>
          </div>
        </div>
      </div>
    </div>
  );
}
